/**
 * InspirationGallery - Filterable gallery of pet portraits
 * Features:
 * - Filter by art style, background, pet type (dynamic from DB with counts)
 * - "Use This Style" action on each item
 * - Connects to upload flow with pre-selected style
 * - Fun entrance animations
 */
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { COLORS, FONT_FAMILY, SHOP_SPACING, CARD_STYLES } from '../ShopStyles';
import { supabase } from '../../supabaseClient';

const InspirationGallery = ({ onNavigate, onUseStyle }) => {
  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dynamic filter options from DB
  const [filterOptions, setFilterOptions] = useState({
    artStyle: [{ id: 'all', name: 'All Styles', count: 0 }],
    petType: [{ id: 'all', name: 'All Pets', count: 0 }],
    background: [{ id: 'all', name: 'All Backgrounds', count: 0 }],
  });
  
  // Inject responsive masonry CSS and entrance animations
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'gallery-masonry-styles';
    styleSheet.textContent = `
      .gallery-masonry {
        column-count: 5;
        column-gap: 16px;
      }
      @media (max-width: 1400px) {
        .gallery-masonry { column-count: 4; }
      }
      @media (max-width: 1100px) {
        .gallery-masonry { column-count: 3; }
      }
      @media (max-width: 800px) {
        .gallery-masonry { column-count: 2; column-gap: 12px; }
      }
      @media (max-width: 500px) {
        .gallery-masonry { column-count: 1; }
      }
      
      /* Responsive filter bar - only wrap on very small screens */
      @media (max-width: 480px) {
        .filter-bar-responsive {
          flex-direction: column !important;
          gap: 8px !important;
        }
        .filter-bar-responsive select {
          width: 100% !important;
          max-width: 280px !important;
        }
      }
      @keyframes popIn {
        0% { 
          opacity: 0; 
          transform: scale(0.8) translateY(20px); 
        }
        60% { 
          transform: scale(1.02) translateY(-5px); 
        }
        100% { 
          opacity: 1; 
          transform: scale(1) translateY(0); 
        }
      }
      .gallery-card-animate {
        animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        opacity: 0;
      }
      .gallery-card-animate:hover {
        transform: scale(1.03) translateY(-6px) rotate(2deg) !important;
        z-index: 10;
      }
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      /* Fun button hover effects - global */
      .fun-button {
        transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
      }
      .fun-button:hover {
        transform: translateY(-3px) scale(1.03) !important;
        box-shadow: 0 8px 25px rgba(147, 51, 234, 0.4) !important;
      }
      .fun-button:active {
        transform: translateY(-1px) scale(1.01) !important;
      }
      .filter-pill:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(147, 51, 234, 0.15) !important;
        border-color: #9333ea !important;
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      const existing = document.getElementById('gallery-masonry-styles');
      if (existing) existing.remove();
    };
  }, []);

  // Filter state
  const [filters, setFilters] = useState({
    artStyle: 'all',
    petType: 'all',
    background: 'all',
  });
  
  // Selected item for detail view
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch filter options with counts from DB
  const fetchFilterOptions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('gallery')
        .select('art_style, pet_type, background')
        .eq('status', 'completed')
        .not('image_url', 'is', null);
      
      if (error) throw error;
      
      // Count occurrences
      const artStyleCounts = {};
      const petTypeCounts = {};
      const backgroundCounts = {};
      
      data?.forEach(item => {
        // Art style
        if (item.art_style) {
          const style = item.art_style.toLowerCase().trim();
          artStyleCounts[style] = (artStyleCounts[style] || 0) + 1;
        }
        // Pet type
        if (item.pet_type) {
          const pet = item.pet_type.toLowerCase().trim();
          petTypeCounts[pet] = (petTypeCounts[pet] || 0) + 1;
        }
        // Background
        if (item.background) {
          const bg = item.background.toLowerCase().trim();
          backgroundCounts[bg] = (backgroundCounts[bg] || 0) + 1;
        }
      });
      
      // Convert to options arrays
      const toOptions = (counts, allLabel) => {
        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        const options = [{ id: 'all', name: allLabel, count: total }];
        
        Object.entries(counts)
          .sort((a, b) => b[1] - a[1]) // Sort by count descending
          .forEach(([name, count]) => {
            if (name && name !== 'none') {
              // Capitalize first letter of each word
              const displayName = name.split(' ')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
              options.push({ id: name, name: displayName, count });
            }
          });
        
        return options;
      };
      
      setFilterOptions({
        artStyle: toOptions(artStyleCounts, '🎨 All Styles'),
        petType: toOptions(petTypeCounts, '🐾 All Pets'),
        background: toOptions(backgroundCounts, '🖼️ All Backgrounds'),
      });
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, []);
  
  // Fetch filter options on mount
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Fetch gallery from Supabase
  const fetchGallery = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('gallery')
        .select('*')
        .eq('status', 'completed')
        .not('image_url', 'is', null)
        .order('created_at', { ascending: false })
        .limit(100);
      
      // Apply filters
      if (filters.artStyle !== 'all') {
        query = query.ilike('art_style', `%${filters.artStyle}%`);
      }
      if (filters.petType !== 'all') {
        query = query.ilike('pet_type', `%${filters.petType}%`);
      }
      if (filters.background !== 'all') {
        query = query.ilike('background', `%${filters.background}%`);
      }
      
      const { data, error: fetchError } = await query;
      
      if (fetchError) throw fetchError;
      
      setGallery(data || []);
    } catch (err) {
      console.error('Error fetching gallery:', err);
      const isDev =
        process.env.NODE_ENV === 'development' ||
        process.env.REACT_APP_ENVIRONMENT === 'development';
      const detail =
        err?.message ||
        err?.error_description ||
        (typeof err === 'string' ? err : '');
      const isNetworkFailure =
        /failed to fetch|networkerror|load failed|network request failed/i.test(
          String(detail)
        );
      let message = 'Failed to load gallery. Please try again.';
      if (isDev && detail) {
        message = `Gallery error: ${detail}`;
        if (isNetworkFailure) {
          message +=
            ' — The app could not reach Supabase over HTTPS. Confirm REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env (no quotes/spaces), restart npm start, check VPN/ad blockers, and that your Supabase project is active.';
        }
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch on mount and filter change
  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  // Handle "Use This Style" action
  const handleUseStyle = (item) => {
    onUseStyle?.({
      artStyle: item.art_style,
      background: item.background,
      costume: item.style,
    });
    // Navigate to upload with pre-selected style
    onNavigate?.('upload', { preSelectedStyle: item.art_style });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>✨ Get Inspired</h1>
        <p style={styles.subtitle}>
          Browse our gallery of pet portraits. Find a style you love!
        </p>
      </div>

      {/* Filter Bar - Pill style like Product Catalog */}
      <div style={styles.filterWrapper}>
        <div style={styles.filterBar} className="filter-bar-responsive">
          <select
            value={filters.artStyle}
            onChange={(e) => handleFilterChange('artStyle', e.target.value)}
            style={styles.filterPill}
            className="filter-pill"
          >
            {filterOptions.artStyle.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name} ({option.count})
              </option>
            ))}
          </select>
          
          {/* Only show pet type filter if we have pet type data */}
          {filterOptions.petType.length > 1 && (
            <select
              value={filters.petType}
              onChange={(e) => handleFilterChange('petType', e.target.value)}
              style={styles.filterPill}
              className="filter-pill"
            >
              {filterOptions.petType.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name} ({option.count})
                </option>
              ))}
            </select>
          )}
          
          <select
            value={filters.background}
            onChange={(e) => handleFilterChange('background', e.target.value)}
            style={styles.filterPillWide}
            className="filter-pill"
          >
            {filterOptions.background.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name} ({option.count})
              </option>
            ))}
          </select>
          
          {(filters.artStyle !== 'all' || filters.petType !== 'all' || filters.background !== 'all') && (
            <button 
              onClick={() => setFilters({ artStyle: 'all', petType: 'all', background: 'all' })} 
              style={styles.clearButton}
              className="fun-button"
            >
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}>🎨</div>
          <p style={styles.loadingText}>Loading inspiration...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error}</p>
          <button onClick={fetchGallery} style={styles.retryButton}>
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && gallery.length === 0 && (
        <div style={styles.emptyContainer}>
          <span style={styles.emptyIcon}>🖼️</span>
          <p style={styles.emptyText}>No portraits found with these filters</p>
          <button
            onClick={() => setFilters({ artStyle: 'all', petType: 'all', background: 'all' })}
            style={styles.clearFiltersButton}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Gallery Masonry */}
      {!loading && !error && gallery.length > 0 && (
        <div className="gallery-masonry" style={styles.galleryMasonry}>
          {gallery.map((item, index) => (
            <GalleryCard
              key={item.id}
              item={item}
              index={index}
              onSelect={() => setSelectedItem(item)}
              onUseStyle={() => handleUseStyle(item)}
            />
          ))}
        </div>
      )}

      {/* Detail Modal - Rendered via Portal to appear above header */}
      {selectedItem && ReactDOM.createPortal(
        <GalleryDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUseStyle={() => {
            handleUseStyle(selectedItem);
            setSelectedItem(null);
          }}
        />,
        document.body
      )}

      {/* Back Button */}
      <button
        onClick={() => onNavigate?.('home')}
        style={styles.backButton}
      >
        ← Back to Home
      </button>
    </div>
  );
};

// Gallery Card Component - Masonry style with entrance animation
const GalleryCard = ({ item, index, onSelect, onUseStyle }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Staggered animation delay (max 1.5s for first 30 items)
  const animationDelay = `${Math.min(index * 0.05, 1.5)}s`;
  
  return (
    <div
      className="gallery-card-animate"
      style={{
        ...styles.card,
        animationDelay,
        transform: isHovered ? 'scale(1.03) translateY(-6px) rotate(2deg)' : 'scale(1) rotate(0deg)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onSelect}
    >
      {/* Rainbow border glow effect on hover */}
      <div style={{
        ...styles.cardGlow,
        opacity: isHovered ? 1 : 0,
      }} />
      
      <div style={styles.cardInner}>
        {/* Image wrapper with clipping */}
        <div style={styles.cardImageWrap}>
          <img
            src={item.image_url}
            alt={item.art_style || 'Pet portrait'}
            style={styles.cardImage}
            loading="lazy"
          />
          
          {/* Hover Overlay with button */}
          <div style={{
            ...styles.cardOverlay,
            opacity: isHovered ? 1 : 0,
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUseStyle();
              }}
              style={styles.useStyleButton}
            >
              ✨ Use This Style
            </button>
          </div>
        </div>
        
        {/* Art style badge - breaks out of card! */}
        {item.art_style && (
          <div style={{
            ...styles.cardStyleBadge,
            transform: isHovered ? 'rotate(-2deg) scale(1.1) translateY(2px)' : 'rotate(-4deg)',
          }}>
            🎨 {item.art_style}
          </div>
        )}
      </div>
    </div>
  );
};

// Detail Modal Component - Transformation showcase layout
const GalleryDetailModal = ({ item, onClose, onUseStyle }) => {
  // Build the prompt sentence
  const buildPromptSentence = () => {
    const parts = [];
    if (item.pet_name) {
      parts.push(<span key="name" style={styles.promptHighlight}>{item.pet_name}</span>);
    } else {
      parts.push(<span key="pet" style={styles.promptHighlight}>{item.pet_type || 'pet'}</span>);
    }
    
    if (item.style && item.style !== 'none') {
      parts.push(' as a ');
      parts.push(<span key="style" style={styles.promptHighlightPink}>{item.style}</span>);
    }
    
    parts.push(' in ');
    parts.push(<span key="art" style={styles.promptHighlightBlue}>{item.art_style || 'artistic'}</span>);
    parts.push(' style');
    
    if (item.background && item.background !== 'none') {
      parts.push(' with a ');
      parts.push(<span key="bg" style={styles.promptHighlightGreen}>{item.background}</span>);
      parts.push(' background');
    }
    
    return parts;
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button onClick={onClose} style={styles.modalClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: 'block' }}>
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        </button>
        
        {/* Generated Portrait - Large */}
        <div style={styles.modalImageSide}>
          <img
            src={item.image_url}
            alt={item.art_style || 'Pet portrait'}
            style={styles.modalImage}
          />
        </div>
        
        {/* Details Panel */}
        <div style={styles.modalDetailsSide}>
          {/* Transformation Section */}
          <div style={styles.transformSection}>
            {/* Original Photo (if available) */}
            {item.original_image_url && (
              <div style={styles.originalPhotoSection}>
                <span style={styles.sectionLabel}>📷 Original Photo</span>
                <div style={styles.originalPhotoWrapper}>
                  <img
                    src={item.original_image_url}
                    alt="Original pet"
                    style={styles.originalPhoto}
                  />
                </div>
              </div>
            )}
            
            {/* Pet Name */}
            <div style={styles.petNameSection}>
              <h2 style={styles.modalPetName}>{item.pet_name || 'Unnamed Cutie'}</h2>
            </div>
            
            {/* Prompt Sentence */}
            <div style={styles.promptSentenceSection}>
              <p style={styles.promptSentence}>
                A portrait of a pet named {buildPromptSentence()}.
              </p>
            </div>
          </div>
          
          {/* CTA Section */}
          <div style={styles.ctaSection}>
            <p style={styles.ctaText}>
              Love this style? Create your own!
            </p>
            <button 
              onClick={onUseStyle} 
              style={styles.modalUseButton}
              className="fun-button"
            >
              ✨ Use This Style
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: `${SHOP_SPACING.lg} 0`,
  },
  header: {
    textAlign: 'center',
    marginBottom: SHOP_SPACING.xl,
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#333',
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: SHOP_SPACING.sm,
  },
  subtitle: {
    fontSize: '18px',
    color: '#666',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  filterWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: SHOP_SPACING.xl,
  },
  filterBar: {
    display: 'inline-flex',
    gap: '10px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 18px',
    paddingRight: '36px',
    fontSize: '15px',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    borderRadius: '25px',
    border: '2px solid #e5e7eb',
    background: '#fff',
    color: '#333',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239333ea' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    whiteSpace: 'nowrap',
  },
  filterPillWide: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 18px',
    paddingRight: '36px',
    fontSize: '15px',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    borderRadius: '25px',
    border: '2px solid #e5e7eb',
    background: '#fff',
    color: '#333',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239333ea' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    whiteSpace: 'nowrap',
  },
  // Old filter styles removed
  refreshButton: {
    padding: '10px 20px',
    fontSize: '15px',
    fontFamily: FONT_FAMILY,
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)',
  },
  // clearButton moved to end of styles
  loadingContainer: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  spinner: {
    fontSize: '60px',
    animation: 'spin 2s linear infinite',
    display: 'inline-block',
  },
  loadingText: {
    fontSize: '18px',
    color: '#666',
    fontFamily: FONT_FAMILY,
    marginTop: SHOP_SPACING.md,
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  errorText: {
    fontSize: '18px',
    color: '#ef4444',
    fontFamily: FONT_FAMILY,
    marginBottom: SHOP_SPACING.md,
  },
  retryButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontFamily: FONT_FAMILY,
    borderRadius: '10px',
    border: 'none',
    background: COLORS.accent,
    color: '#fff',
    cursor: 'pointer',
    fontWeight: '600',
  },
  emptyContainer: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: '60px',
    display: 'block',
    marginBottom: SHOP_SPACING.md,
    opacity: 0.5,
  },
  emptyText: {
    fontSize: '18px',
    color: '#666',
    fontFamily: FONT_FAMILY,
    marginBottom: SHOP_SPACING.md,
  },
  clearFiltersButton: {
    padding: '12px 24px',
    fontSize: '16px',
    fontFamily: FONT_FAMILY,
    borderRadius: '10px',
    border: '2px solid #9333ea',
    background: 'transparent',
    color: '#9333ea',
    cursor: 'pointer',
    fontWeight: '600',
  },
  galleryMasonry: {
    columnCount: 5,
    columnGap: '16px',
    marginBottom: SHOP_SPACING.xl,
  },
  card: {
    breakInside: 'avoid',
    marginBottom: '28px',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.35s ease',
    position: 'relative',
    padding: '3px',
    overflow: 'visible',
    transformOrigin: 'center center',
  },
  cardGlow: {
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #ff6b6b, #feca57, #48dbfb, #ff9ff3, #54a0ff, #5f27cd)',
    backgroundSize: '300% 300%',
    animation: 'gradientShift 3s ease infinite',
    transition: 'opacity 0.3s ease',
    zIndex: 0,
  },
  cardInner: {
    position: 'relative',
    borderRadius: '14px',
    overflow: 'visible', // Allow badge to break out
    background: '#fff',
    zIndex: 1,
  },
  cardImageWrap: {
    position: 'relative',
    borderRadius: '14px',
    overflow: 'hidden', // Clip the image
  },
  cardImage: {
    width: '100%',
    display: 'block',
  },
  cardStyleBadge: {
    position: 'absolute',
    bottom: '-8px', // Breaking the plane!
    left: '10px',
    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
    padding: '6px 10px',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(147, 51, 234, 0.35)',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    transform: 'rotate(-4deg)',
    zIndex: 20,
  },
  cardOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    transition: 'opacity 0.3s ease',
  },
  useStyleButton: {
    padding: '12px 24px',
    fontSize: '15px',
    fontFamily: FONT_FAMILY,
    borderRadius: '50px',
    border: 'none',
    background: '#fff',
    color: '#333',
    cursor: 'pointer',
    fontWeight: '700',
    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
    transition: 'all 0.2s ease',
  },
  backButton: {
    display: 'block',
    margin: '0 auto',
    background: 'transparent',
    border: 'none',
    color: '#666',
    fontSize: '16px',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    padding: SHOP_SPACING.md,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    backdropFilter: 'blur(12px)',
    zIndex: 9999, // Above header
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SHOP_SPACING.xl,
  },
  modalContent: {
    display: 'flex',
    maxWidth: '950px',
    width: '100%',
    maxHeight: '90vh',
    position: 'relative',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
    background: '#fff',
  },
  modalClose: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    zIndex: 10,
    appearance: 'none',
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'rgba(255,255,255,0.9)',
    width: '40px',
    height: '40px',
    minWidth: '40px',
    minHeight: '40px',
    maxWidth: '40px',
    maxHeight: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    lineHeight: 1,
    fontSize: '16px',
    boxSizing: 'border-box',
  },
  modalImageSide: {
    flex: '1 1 60%',
    background: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: 0,
    margin: 0,
  },
  modalImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  modalDetailsSide: {
    flex: '0 0 320px',
    background: 'linear-gradient(180deg, #faf5ff 0%, #fff 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '28px',
    overflow: 'hidden',
  },
  
  // Transformation section
  transformSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  originalPhotoSection: {
    marginBottom: '20px',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#9333ea',
    fontFamily: FONT_FAMILY,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
    display: 'block',
  },
  originalPhotoWrapper: {
    width: '100px',
    height: '100px',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '3px solid #fff',
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  },
  originalPhoto: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  petNameSection: {
    marginBottom: '16px',
  },
  modalPetName: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#7c3aed',
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  promptSentenceSection: {
    marginBottom: '20px',
  },
  promptSentence: {
    fontSize: '16px',
    color: '#6b7280',
    fontFamily: FONT_FAMILY,
    lineHeight: 1.7,
    margin: 0,
  },
  promptHighlight: {
    color: '#7c3aed',
    fontWeight: '700',
  },
  promptHighlightPink: {
    color: '#ec4899',
    fontWeight: '700',
  },
  promptHighlightBlue: {
    color: '#0ea5e9',
    fontWeight: '700',
  },
  promptHighlightGreen: {
    color: '#22c55e',
    fontWeight: '700',
  },
  modalStyleBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
    color: '#fff',
    padding: '10px 16px',
    borderRadius: '12px',
    transform: 'rotate(-1deg)',
    boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
    alignSelf: 'flex-start',
  },
  modalStyleIcon: {
    fontSize: '18px',
  },
  modalStyleText: {
    fontSize: '14px',
    fontWeight: '700',
    fontFamily: FONT_FAMILY,
  },
  
  // CTA Section
  ctaSection: {
    paddingTop: '20px',
    borderTop: '1px solid rgba(147, 51, 234, 0.1)',
  },
  ctaText: {
    fontSize: '15px',
    color: '#374151',
    fontFamily: FONT_FAMILY,
    fontWeight: '500',
    lineHeight: 1.5,
    margin: 0,
    marginBottom: '16px',
  },
  modalUseButton: {
    width: '100%',
    padding: '18px 24px',
    fontSize: '17px',
    fontFamily: FONT_FAMILY,
    borderRadius: '14px',
    border: 'none',
    fontWeight: '700',
    cursor: 'pointer',
    background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
    color: '#fff',
    boxShadow: '0 6px 20px rgba(147, 51, 234, 0.4)',
  },
  clearButton: {
    padding: '10px 16px',
    fontSize: '14px',
    fontFamily: FONT_FAMILY,
    borderRadius: '25px',
    border: '2px solid #ef4444',
    background: '#fff',
    color: '#ef4444',
    cursor: 'pointer',
    fontWeight: '600',
  },
};

export default InspirationGallery;

